import { useState, useEffect, useCallback } from "react";
import apiClient from "@/lib/apiClient";

const ROLES = ["medecin", "pharmacien", "admin"];

function RoleBadge({ role }) {
  const colors = {
    admin: "bg-purple-100 text-purple-800",
    medecin: "bg-blue-100 text-blue-800",
    pharmacien: "bg-teal-100 text-teal-800",
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors[role] ?? "bg-slate-100 text-slate-600"}`}>
      {role}
    </span>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", name: "", role: "medecin", notes: "" });
  const [adding, setAdding] = useState(false);
  const [formError, setFormError] = useState("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get("/admin/users");
      setUsers(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Accès réservé aux administrateurs.");
      } else {
        setError("Impossible de charger les utilisateurs.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    setFormError("");
    try {
      await apiClient.post("/admin/users", form);
      setForm({ email: "", name: "", role: "medecin", notes: "" });
      await fetchUsers();
    } catch (err) {
      setFormError(err.response?.data?.detail ?? "Erreur lors de l'ajout.");
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (email) => {
    try {
      await apiClient.patch(`/admin/users/${encodeURIComponent(email)}/toggle`);
      await fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail ?? "Erreur lors de la modification.");
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Supprimer définitivement ${email} ?`)) return;
    try {
      await apiClient.delete(`/admin/users/${encodeURIComponent(email)}`);
      await fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail ?? "Erreur lors de la suppression.");
    }
  };

  if (error) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">

      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Gestion des utilisateurs</h1>
        <p className="text-sm text-slate-500 mt-1">
          Seuls les utilisateurs listés ici peuvent se connecter via Google.
        </p>
      </div>

      {/* Formulaire ajout */}
      <form onSubmit={handleAdd} className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <h2 className="text-base font-semibold text-slate-700">Ajouter un utilisateur</h2>

        {formError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            {formError}
          </p>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Adresse email Google
            </label>
            <input
              type="email"
              required
              placeholder="prenom.nom@hopital.fr"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Nom complet
            </label>
            <input
              type="text"
              required
              placeholder="Dr Prénom Nom"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Rôle</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Notes (optionnel)
            </label>
            <input
              type="text"
              placeholder="CHU Paris, service gériatrie..."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={adding}
          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
        >
          {adding ? "Ajout en cours..." : "Ajouter l'utilisateur"}
        </button>
      </form>

      {/* Liste */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-700">
            Utilisateurs autorisés
            <span className="ml-2 text-xs font-normal text-slate-400">
              {users.length} compte{users.length !== 1 ? "s" : ""}
            </span>
          </h2>
          <button
            onClick={fetchUsers}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
          >
            Rafraîchir
          </button>
        </div>

        {loading ? (
          <div className="px-6 py-8 text-center text-sm text-slate-400">
            Chargement...
          </div>
        ) : users.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-slate-400">
            Aucun utilisateur. Ajoutez le premier ci-dessus.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase tracking-wide bg-slate-50">
                <th className="px-6 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Nom</th>
                <th className="px-4 py-3 text-left">Rôle</th>
                <th className="px-4 py-3 text-left">Statut</th>
                <th className="px-4 py-3 text-left">Ajouté par</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((user) => (
                <tr key={user.email} className={`${!user.active ? "opacity-50" : ""}`}>
                  <td className="px-6 py-3 font-mono text-xs text-slate-700">
                    {user.email}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{user.name}</td>
                  <td className="px-4 py-3">
                    <RoleBadge role={user.role} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${user.active ? "text-green-700" : "text-slate-400"}`}>
                      {user.active ? "✓ Actif" : "Inactif"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {user.added_by === "system" ? "système" : user.added_by}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {user.role !== "admin" && (
                      <>
                        <button
                          onClick={() => handleToggle(user.email)}
                          className="text-xs text-slate-600 hover:text-blue-700 underline"
                        >
                          {user.active ? "Désactiver" : "Activer"}
                        </button>
                        <button
                          onClick={() => handleDelete(user.email)}
                          className="text-xs text-red-500 hover:text-red-700 underline"
                        >
                          Supprimer
                        </button>
                      </>
                    )}
                    {user.role === "admin" && (
                      <span className="text-xs text-slate-400 italic">Protégé</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Note sécurité */}
      <div className="border-l-2 border-blue-400 bg-blue-50 px-4 py-3 rounded-r-lg">
        <p className="text-xs text-blue-800">
          <strong>Sécurité :</strong> Les comptes admin (définis via <code className="bg-blue-100 px-1 rounded">ADMIN_EMAILS</code>)
          ne peuvent pas être désactivés ou supprimés depuis cette interface.
          Toute modification des admins se fait exclusivement via les variables d'environnement du serveur.
        </p>
      </div>
    </div>
  );
}
